// AI Consent Gate — Apple App Review 5.1.1 / 5.1.2 compliance.
//
// Apple requires that we explicitly ask permission before sharing user data
// with third-party AI providers (OpenAI, Google Gemini). This module wraps
// every AI-triggering action behind an `ensureAIConsent(lang)` promise:
//
//   const ok = await ensureAIConsent(lang);
//   if (!ok) return;      // user declined — abort the API call
//
// TO DISABLE THIS GATE AFTER APPLE APPROVES:
// -----------------------------------------
// Flip the `AI_CONSENT_REQUIRED` flag below to `false`. The gate then
// resolves `true` immediately and the modal never appears. The wiring in
// AICoach.jsx / AddMeal.jsx / Profile.jsx stays as-is (safe no-op).
//
// GEO EXCLUSIONS
// --------------
// Owner asked to hide the modal from users in the Gulf launch markets so
// our primary audience doesn't get consent friction — Apple reviewers
// (Cupertino, timezone America/Los_Angeles) don't match any Gulf timezone
// so they still see the sheet. Add / remove ISO codes in
// AI_CONSENT_EXCLUDED_COUNTRIES; detection reuses the existing
// timezone-based mapping from /lib/gulfCountry.js (no network calls).

import { detectGulfCountry } from "@/lib/gulfCountry";

export const AI_CONSENT_REQUIRED = false;

// ISO-3166 alpha-2. Users whose timezone maps to any of these countries
// will bypass the consent modal entirely (and won't see the Profile card).
export const AI_CONSENT_EXCLUDED_COUNTRIES = new Set(["SA", "OM", "QA", "KW"]);

const STORAGE_KEY = "calofay_ai_consent_v1";

/** True if the current user's country is on the exclusion list. */
export function isAIConsentExcluded() {
  try {
    const cc = detectGulfCountry();
    return !!cc && AI_CONSENT_EXCLUDED_COUNTRIES.has(cc);
  } catch {
    return false;
  }
}

export function hasAIConsent() {
  if (!AI_CONSENT_REQUIRED) return true;
  if (isAIConsentExcluded()) return true;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return parsed?.accepted === true;
  } catch {
    return false;
  }
}

export function setAIConsent(accepted) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ accepted: !!accepted, at: new Date().toISOString() }),
    );
  } catch {}
  // Notify any listeners (Settings toggle etc.) that consent changed.
  window.dispatchEvent(new CustomEvent("ai-consent-changed", { detail: { accepted: !!accepted } }));
}

export function revokeAIConsent() {
  try { localStorage.removeItem(STORAGE_KEY); } catch {}
  window.dispatchEvent(new CustomEvent("ai-consent-changed", { detail: { accepted: false } }));
}

// Promise-based gate. Resolves true if the user already accepted (or if the
// feature flag is off, or if the user is in an excluded country), otherwise
// dispatches an event that the mounted AIConsentModal listens to. The modal
// calls back with the user's choice and this promise resolves accordingly.
export function ensureAIConsent(lang = "ar") {
  if (!AI_CONSENT_REQUIRED) return Promise.resolve(true);
  if (isAIConsentExcluded()) return Promise.resolve(true);
  if (hasAIConsent()) return Promise.resolve(true);
  return new Promise((resolve) => {
    const handler = (e) => {
      window.removeEventListener("ai-consent-resolved", handler);
      resolve(!!e.detail?.accepted);
    };
    window.addEventListener("ai-consent-resolved", handler);
    window.dispatchEvent(new CustomEvent("ai-consent-request", { detail: { lang } }));
  });
}
